import { useEffect } from 'react';
import { ApolloError } from '@apollo/client';

import Config from '../../lib/config';
import { getJwtExpiryMs } from '../../lib/jwtExpiry';
import { Environment } from '../types/config';

// How long before the Intercom token expires to fetch a replacement. Large enough to absorb a slow
// meProvider round trip, small enough that we are not re-fetching provider data for no reason.
export const INTERCOM_JWT_REFRESH_BUFFER_MS = 5 * 60 * 1000;

// Floor on the scheduled delay. Expiry is compared against the local clock, so a device set far
// enough ahead of the server sees every freshly minted token as already due, and each refresh
// produces another one — without a floor that is a tight re-fetch loop against the API. This bounds
// the pathological case to one request a minute; it does not make the clock correct.
export const INTERCOM_JWT_MIN_REFRESH_DELAY_MS = 60 * 1000;

// Delay before the first retry of a refresh that failed, doubling on each further failure.
export const INTERCOM_JWT_REFRESH_RETRY_BASE_MS = 30 * 1000;

// How many times to retry a failed refresh before giving up. With the base delay above this spends
// roughly fifteen minutes across five attempts — long enough to ride out a deploy or a brief
// outage, bounded so a server that is down for the afternoon does not leave every open tab
// retrying for the life of the session. Past the budget, recovery comes from the visibility
// listener below (any tab switch) or a reload.
export const INTERCOM_JWT_REFRESH_MAX_RETRIES = 5;

/**
 * The subset of `refreshAuthedProviderUser`'s return value this hook reads. Declared structurally
 * rather than importing Apollo's `QueryResult` so the hook can be exercised without building a full
 * query result — the real `AuthContext` value satisfies it.
 */
interface RefreshAuthedProviderUserResult {
  error?: ApolloError;
}

export type RefreshAuthedProviderUser = (options?: {
  context?: Record<string, unknown>;
}) => Promise<RefreshAuthedProviderUserResult>;

// Keeps `errorLink` from raising a connection-error toast for a refresh the provider never asked
// for. See `src/lib/apolloHelpers.ts`.
const BACKGROUND_REFRESH_OPTIONS = {
  context: { suppressErrorToast: true },
};

interface UseIntercomJwtRefreshProps {
  intercomUserJwt: string | null | undefined;
  refreshAuthedProviderUser: RefreshAuthedProviderUser;
}

/**
 * Keeps the Intercom Messenger identity token fresh for a long-running session.
 *
 * Intercom keeps whatever token it was last handed, and nothing re-sends one on its own. A provider
 * who leaves the app open past the token's expiry would therefore keep presenting a stale one:
 * harmless while Messenger Security is unenforced, but a rejected Messenger session once it is. So
 * re-fetch meProvider shortly before expiry — the identify effect in `App` depends on
 * `authedProviderUser` and re-identifies with the fresh token on its own.
 *
 * Checked on tab focus as well as on a timer, because browsers throttle and suspend timers in
 * background tabs. Over a token lifetime measured in hours, the timer alone will not reliably fire.
 */
export const useIntercomJwtRefresh = ({
  intercomUserJwt,
  refreshAuthedProviderUser,
}: UseIntercomJwtRefreshProps): void => {
  useEffect(() => {
    if (
      Config.REACT_APP_ENVIRONMENT === Environment.Development ||
      !intercomUserJwt
    ) {
      return;
    }

    const expiresAtMs = getJwtExpiryMs(intercomUserJwt);
    if (expiresAtMs === null) {
      return;
    }

    const refreshAtMs = expiresAtMs - INTERCOM_JWT_REFRESH_BUFFER_MS;

    let cancelled = false;
    let inFlight = false;
    let retries = 0;
    let timer: ReturnType<typeof setTimeout>;

    const refreshIfDue = async () => {
      if (
        cancelled ||
        inFlight ||
        document.hidden ||
        Date.now() < refreshAtMs
      ) {
        return;
      }

      inFlight = true;
      let failed = true;
      try {
        const { error } = await refreshAuthedProviderUser(
          BACKGROUND_REFRESH_OPTIONS,
        );
        failed = Boolean(error);
      } catch {
        failed = true;
      } finally {
        inFlight = false;
      }

      // A successful refresh mints a new token, which re-runs this effect and schedules the next
      // expiry from scratch — nothing to do here. A failed one changes nothing the effect depends
      // on, so without rescheduling here the provider keeps the expiring token.
      if (failed && !cancelled && retries < INTERCOM_JWT_REFRESH_MAX_RETRIES) {
        // A tab switch can run an attempt while an earlier failure's retry is still armed. Dropping
        // that handle without cancelling it would leave two chains drawing on one `retries` budget,
        // clustering the requests and spending the budget well before the backoff intends.
        clearTimeout(timer);
        timer = setTimeout(
          refreshIfDue,
          INTERCOM_JWT_REFRESH_RETRY_BASE_MS * 2 ** retries,
        );
        retries += 1;
      }
    };

    timer = setTimeout(
      refreshIfDue,
      Math.max(refreshAtMs - Date.now(), INTERCOM_JWT_MIN_REFRESH_DELAY_MS),
    );
    document.addEventListener('visibilitychange', refreshIfDue);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', refreshIfDue);
    };
  }, [intercomUserJwt, refreshAuthedProviderUser]);
};
